import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowLeftRight, ChevronRight, Eye, FileSearch, RotateCw, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Banner } from '../../components/Banner.tsx';
import { Button } from '../../components/Button.tsx';
import { formatDateTime, shortId } from '../../components/format.ts';
import { CompareLens } from '../../components/CompareLens.tsx';
import { EmptyState } from '../../components/EmptyState.tsx';
import { Inspector, type InspectorKind } from '../../components/Inspector.tsx';
import { SelectField } from '../../components/SelectField.tsx';
import { Skeleton } from '../../components/Skeleton.tsx';
import { TextField } from '../../components/TextField.tsx';
import { useCan } from '../../features/auth/permissions.ts';
import { readOwner } from '../../features/authorization/model.ts';
import { dnsOutcomeText } from '../../features/discovery/evidenceSummary.ts';
import { evidenceToCompareItem } from '../../features/findings/compare.ts';
import {
  evidenceToInspectorDetail,
  filterEvidence,
  findingToInspectorDetail,
  selectWorkspaceEvidenceCounts,
} from '../../features/findings/model.ts';
import { useWorkspaceFindings } from '../../features/findings/store.ts';
import type { Evidence, SourceRecord } from '../../features/findings/types.ts';
import { useSession } from '../../state/session.ts';
import { ImportExperience } from './ImportExperience.tsx';
import {
  collectorLabel,
  collectorText,
  neutralPointer,
} from '../../features/discovery/collectorLabel.ts';
import styles from './EvidencePage.module.css';

export interface EvidenceRouteSearch {
  q?: string;
  kind?: string;
  origin?: string;
  selected?: string;
  inspector?: string;
  id?: string;
  compare?: string;
  base?: string;
  target?: string;
}

export function EvidencePage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as EvidenceRouteSearch;
  const isCompareOpen = String(search.compare) === 'true';

  const workspace = useSession((s) => s.workspace);
  const canWrite = useCan('workspace.write');

  const {
    evidence,
    sourceRecords,
    findings,
    loading,
    loaded,
    stale,
    error,
    loadLatestAttemptFindings,
  } = useWorkspaceFindings();

  const workspaceId = workspace?.id;
  const ownerToken = workspace?.authorizationId ? readOwner(workspace.authorizationId) : '';
  const setupSessionId = workspace?.setupSessionId ?? '';
  const readToken = ownerToken;
  const credentials = useMemo(
    () => ({ session: setupSessionId, token: readToken }),
    [setupSessionId, readToken],
  );

  useEffect(() => {
    if (!workspaceId) return;
    void loadLatestAttemptFindings(workspaceId, credentials, { includeRetainedEvidence: true });
  }, [workspaceId, credentials, loadLatestAttemptFindings]);

  const refreshEvidence = useCallback(() => {
    if (workspaceId)
      void loadLatestAttemptFindings(workspaceId, credentials, { includeRetainedEvidence: true });
  }, [workspaceId, credentials, loadLatestAttemptFindings]);

  // Search parameters and navigation updates
  const updateSearch = useCallback(
    (newParams: Partial<EvidenceRouteSearch>) => {
      const merged = { ...search, ...newParams };
      const cleaned: Record<string, string> = {};
      for (const [key, val] of Object.entries(merged)) {
        if (val !== undefined && val !== '' && val !== 'ALL') {
          cleaned[key] = String(val);
        }
      }
      void navigate({
        to: '/evidence',
        search: cleaned,
        replace: true,
      });
    },
    [navigate, search],
  );

  // Inspector selection handling
  const [selectedInspector, setSelectedInspector] = useState<{
    kind: 'evidence' | 'finding';
    id: string;
  } | null>(() => {
    if (search.inspector && search.id) {
      return { kind: search.inspector as 'evidence' | 'finding', id: search.id };
    }
    return null;
  });

  const activeInspector = useMemo(() => {
    return (
      selectedInspector ||
      (search.inspector && search.id
        ? { kind: search.inspector as 'evidence' | 'finding', id: search.id }
        : null)
    );
  }, [selectedInspector, search.inspector, search.id]);

  const inspectorOpen = Boolean(activeInspector);

  // Responsive inspector presentation mode
  const [inspectorMode, setInspectorMode] = useState<'dock' | 'drawer' | 'full'>('dock');
  useEffect(() => {
    const updateMode = () => {
      const w = window.innerWidth;
      if (w >= 1100) setInspectorMode('dock');
      else if (w >= 1024) setInspectorMode('drawer');
      else setInspectorMode('full');
    };
    updateMode();
    window.addEventListener('resize', updateMode);
    return () => window.removeEventListener('resize', updateMode);
  }, []);

  const inspectedEvidence = useMemo(() => {
    if (activeInspector?.kind === 'evidence') {
      const ev = evidence.find((e) => e.evidenceId === activeInspector.id);
      if (ev) {
        const src = sourceRecords.find((s) => s.sourceRecordId === ev.sourceRecordId);
        return evidenceToInspectorDetail(ev, src, null, findings);
      }
    }
    return null;
  }, [activeInspector, evidence, sourceRecords, findings]);

  const inspectedFinding = useMemo(() => {
    if (activeInspector?.kind === 'finding') {
      const f = findings.find((item) => item.findingId === activeInspector.id);
      if (f) return findingToInspectorDetail(f);
    }
    return null;
  }, [activeInspector, findings]);

  const lastFocusedTriggerRef = useRef<HTMLElement | null>(null);

  const handleOpenEvidenceInspector = (evId: string) => {
    lastFocusedTriggerRef.current = document.activeElement as HTMLElement | null;
    setSelectedInspector({ kind: 'evidence', id: evId });
    updateSearch({ inspector: 'evidence', id: evId, selected: evId });
  };

  const handleCloseInspector = () => {
    setSelectedInspector(null);
    updateSearch({ inspector: undefined, id: undefined });
    if (lastFocusedTriggerRef.current && document.contains(lastFocusedTriggerRef.current)) {
      lastFocusedTriggerRef.current.focus();
    }
  };

  // Derive counts
  const counts = useMemo(
    () => selectWorkspaceEvidenceCounts(evidence, sourceRecords, findings),
    [evidence, sourceRecords, findings],
  );

  // Filter evidence
  const filteredEvidence = useMemo(() => {
    return filterEvidence(
      evidence,
      {
        query: search.q,
        kind: search.kind,
        sourceClass: search.origin as 'RAW_TOOL_OUTPUT' | 'MODEL_ASSISTED' | 'ALL' | undefined,
      },
      findings,
      sourceRecords,
    );
  }, [evidence, search.q, search.kind, search.origin, findings, sourceRecords]);

  const hasActiveFilters = Boolean(
    search.q ||
    (search.kind && search.kind !== 'ALL') ||
    (search.origin && search.origin !== 'ALL'),
  );

  const handleClearFilters = () => {
    updateSearch({ q: undefined, kind: undefined, origin: undefined });
  };

  const sourceRecordMap = useMemo(() => {
    return new Map<string, SourceRecord>(sourceRecords.map((sr) => [sr.sourceRecordId, sr]));
  }, [sourceRecords]);

  // Every DNS outcome, errors from DNS sources included, reads as one row that expands.
  const isDns = useCallback(
    (ev: Evidence) =>
      ev.kind === 'DNS_RECORD' ||
      (ev.kind === 'ERROR_RECORD' &&
        collectorLabel(sourceRecordMap.get(ev.sourceRecordId)?.sourceSystem) === 'DNS resolver'),
    [sourceRecordMap],
  );
  const dnsEvidence = useMemo(() => filteredEvidence.filter(isDns), [filteredEvidence, isDns]);
  const groupDns = dnsEvidence.length > 1;
  // Untouched, the group is open only when a link selects one of its records.
  const [dnsToggled, setDnsToggled] = useState<boolean | null>(null);
  const dnsOpen = dnsToggled ?? dnsEvidence.some((ev) => ev.evidenceId === search.selected);
  const dnsSources = [
    ...new Set(
      dnsEvidence.flatMap((ev) => sourceRecordMap.get(ev.sourceRecordId)?.sourceSystem ?? []),
    ),
  ];
  const dnsCiting = findings.filter((f) =>
    dnsEvidence.some((ev) => f.evidenceIds.includes(ev.evidenceId)),
  );
  const dnsLatest = dnsEvidence
    .map((ev) => ev.capturedAt)
    .sort()
    .at(-1);

  const compareEvidenceList = useMemo(() => {
    return evidence.map((ev) => {
      const src = sourceRecordMap.get(ev.sourceRecordId);
      return evidenceToCompareItem(ev, src, findings);
    });
  }, [evidence, sourceRecordMap, findings]);

  const renderRow = (ev: Evidence, nested = false) => {
    const src = sourceRecordMap.get(ev.sourceRecordId);
    const isSelected = search.selected === ev.evidenceId;
    const citing = findings.filter((f) => f.evidenceIds.includes(ev.evidenceId));

    return (
      <tr
        key={ev.evidenceId}
        data-selected={isSelected ? 'true' : undefined}
        data-nested={nested || undefined}
      >
        <td data-label="Record">
          <p className={styles.cardTitle}>{ev.summary || neutralPointer(ev.sourcePointer)}</p>
          <p className={styles.recordMeta}>
            <span>{ev.kind.toLowerCase().replaceAll('_', ' ')}</span>
            {/* Several hosts resolve to one address: the host tells those rows apart. */}
            {src?.subject && !ev.summary?.includes(src.subject) && <span>{src.subject}</span>}
            {src && <span>{collectorText(src.sourceSystem, src.sourceVersion)}</span>}
            <code className={styles.evidenceId} title={ev.evidenceId}>
              {shortId(ev.evidenceId)}
            </code>
          </p>
        </td>
        <td data-label="Captured">
          <time dateTime={ev.capturedAt}>{formatDateTime(ev.capturedAt)}</time>
        </td>
        <td data-label="Supports">
          {citing.length > 0 ? (
            <div className={styles.citingRow}>
              {citing.map((f) => (
                <Link
                  key={f.findingId}
                  to="/findings/$findingId"
                  params={{ findingId: f.findingId }}
                  className={styles.findingLink}
                >
                  {f.title}
                </Link>
              ))}
            </div>
          ) : (
            'None recorded'
          )}
        </td>
        <td data-label="Actions">
          <div className={styles.cardActions}>
            <Button
              variant="tertiary"
              size="small"
              onPress={() => {
                if (!isCompareOpen) updateSearch({ compare: 'true', base: ev.evidenceId });
                else if (!search.base) updateSearch({ base: ev.evidenceId });
                else updateSearch({ target: ev.evidenceId });
              }}
              aria-label={`Compare evidence ${ev.evidenceId}`}
            >
              <ArrowLeftRight size={16} aria-hidden="true" />
            </Button>
            <Button
              variant="secondary"
              size="small"
              onPress={() => handleOpenEvidenceInspector(ev.evidenceId)}
              aria-label={`Inspect evidence ${ev.evidenceId}`}
            >
              <Eye size={14} aria-hidden="true" />
              Inspect
            </Button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className={styles.page} data-workbench>
      {/* Page Header */}
      <div className={styles.headingRow}>
        <header className={styles.header}>
          <h1 className={styles.title}>Evidence</h1>
        </header>

        <div className={styles.headerActions}>
          <Button
            variant={isCompareOpen ? 'primary' : 'secondary'}
            size="small"
            onPress={() =>
              updateSearch({
                compare: isCompareOpen ? undefined : 'true',
              })
            }
            aria-label={isCompareOpen ? 'Close Compare lens' : 'Open Compare lens'}
            aria-expanded={isCompareOpen}
          >
            <ArrowLeftRight size={14} aria-hidden="true" />
            <span>Compare lens</span>
          </Button>

          <Button
            variant="secondary"
            size="small"
            onPress={refreshEvidence}
            isDisabled={loading}
            aria-label="Refresh evidence records"
          >
            <RotateCw size={14} aria-hidden="true" />
            <span>Refresh</span>
          </Button>
        </div>
      </div>

      {stale && (
        <Banner tone="attention" icon="warning" title="Evidence data may be stale">
          We can’t reach Traceira right now. The last confirmed evidence records are shown.
        </Banner>
      )}

      {error && !stale && (
        <Banner tone="negative" icon="warning" title="Could not load evidence records">
          {error.message}
        </Banner>
      )}

      {/* Filters on an empty collection are noise; they return once records or a filter exist. */}
      {!(loaded && evidence.length === 0 && !hasActiveFilters) && (
        <section className={styles.filterToolbar} aria-label="Evidence filters">
          <div className={styles.searchField}>
            <TextField
              label="Search evidence"
              value={search.q ?? ''}
              onChange={(q) => updateSearch({ q: q || undefined })}
              placeholder="Search ID, source pointer, or summary"
            />
          </div>

          <div className={styles.filterSelect}>
            <SelectField
              label="Evidence kind"
              value={search.kind ?? 'ALL'}
              onChange={(kind) => updateSearch({ kind })}
              options={
                [
                  ['ALL', 'All kinds'],
                  ['DNS_RECORD', 'DNS records'],
                  ['NETWORK_ENDPOINT', 'Network endpoints'],
                  ['RAW_TOOL_RECORD', 'Raw tool records'],
                  ['LOG_EXCERPT', 'Log excerpts'],
                  ['ERROR_RECORD', 'Error records'],
                ] as const
              }
            />
          </div>

          <div className={styles.filterSelect}>
            <SelectField
              label="Source"
              value={search.origin ?? 'ALL'}
              onChange={(origin) => updateSearch({ origin })}
              options={
                [
                  ['ALL', 'All sources'],
                  ['RAW_TOOL_OUTPUT', 'Raw tool output'],
                  ['MODEL_ASSISTED', 'AI interpretation'],
                ] as const
              }
            />
          </div>

          {hasActiveFilters && (
            <Button
              variant="tertiary"
              size="small"
              onPress={handleClearFilters}
              className={styles.clearFilterBtn}
            >
              <X size={14} aria-hidden="true" />
              <span>Clear filters</span>
            </Button>
          )}
        </section>
      )}

      {/* Compare Lens section (FE-063) */}
      {isCompareOpen && (
        <section className={styles.compareSection} aria-label="Compare lens workspace">
          <CompareLens
            evidenceList={compareEvidenceList}
            selectedBaselineId={search.base ?? null}
            selectedTargetId={search.target ?? null}
            onSelectBaseline={(baseId) => updateSearch({ base: baseId ?? undefined })}
            onSelectTarget={(targetId) => updateSearch({ target: targetId ?? undefined })}
            onClose={() => updateSearch({ compare: undefined, base: undefined, target: undefined })}
          />
        </section>
      )}

      <div className={styles.statusRow}>
        {evidence.length > 0 && (
          <p className={styles.resultCount} role="status">
            {filteredEvidence.length} of {counts.total} evidence records
          </p>
        )}
        {canWrite && <ImportExperience />}
      </div>
      {/* Main Content Layout (Evidence List + optional Docked Inspector) */}
      <div
        className={styles.contentLayout}
        data-has-inspector={inspectorOpen && inspectorMode === 'dock'}
      >
        <div className={styles.evidenceStage}>
          {loading && !loaded ? (
            <Skeleton label="Loading evidence records…" />
          ) : evidence.length === 0 ? (
            <EmptyState
              title="No evidence records yet"
              description="Collected records will appear here with their sources and related findings."
              variant="not-started"
              icon={FileSearch}
            />
          ) : filteredEvidence.length === 0 ? (
            <EmptyState
              title="No matching evidence"
              description="No evidence records match the selected filters. Clear your search or filters to see all collected records."
              variant="filtered-empty"
              action={{
                label: 'Clear filters',
                onPress: handleClearFilters,
              }}
            />
          ) : (
            <div className={styles.ledger}>
              <table className={styles.register} aria-label="Evidence records">
                <thead>
                  <tr>
                    <th scope="col">Record</th>
                    <th scope="col">Captured</th>
                    <th scope="col">Supports</th>
                    <th scope="col">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {groupDns && (
                    <tr data-group="dns">
                      <td data-label="Record">
                        <p className={styles.cardTitle}>
                          DNS lookups · {dnsOutcomeText(dnsEvidence)}
                        </p>
                        <p className={styles.recordMeta}>
                          <span>dns</span>
                          {dnsSources.map((name) => (
                            <span key={name}>{collectorLabel(name)}</span>
                          ))}
                        </p>
                      </td>
                      <td data-label="Captured">
                        {dnsLatest && <time dateTime={dnsLatest}>{formatDateTime(dnsLatest)}</time>}
                      </td>
                      <td data-label="Supports">
                        {dnsCiting.length > 0 ? (
                          <div className={styles.citingRow}>
                            {dnsCiting.map((f) => (
                              <Link
                                key={f.findingId}
                                to="/findings/$findingId"
                                params={{ findingId: f.findingId }}
                                className={styles.findingLink}
                              >
                                {f.title}
                              </Link>
                            ))}
                          </div>
                        ) : (
                          'None recorded'
                        )}
                      </td>
                      <td data-label="Actions">
                        <div className={styles.cardActions}>
                          <Button
                            variant="secondary"
                            size="small"
                            aria-expanded={dnsOpen}
                            onPress={() => setDnsToggled(!dnsOpen)}
                          >
                            <ChevronRight
                              size={14}
                              aria-hidden="true"
                              className={styles.groupChevron}
                              data-open={dnsOpen || undefined}
                            />
                            {dnsOpen ? 'Hide records' : `Show ${dnsEvidence.length} records`}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )}
                  {groupDns && dnsOpen && dnsEvidence.map((ev) => renderRow(ev, true))}
                  {filteredEvidence
                    .filter((ev) => !groupDns || !isDns(ev))
                    .map((ev) => renderRow(ev))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Docked Inspector (>=1100px) */}
        {inspectorOpen && inspectorMode === 'dock' && (
          <aside aria-label="Evidence inspector">
            <Inspector
              selection={{
                kind: (activeInspector?.kind as InspectorKind) || 'evidence',
                id: activeInspector?.id ?? '',
              }}
              mode="dock"
              evidence={inspectedEvidence}
              finding={inspectedFinding}
              onClose={handleCloseInspector}
              onSelectFinding={(fId) => {
                setSelectedInspector({ kind: 'finding', id: fId });
                updateSearch({ inspector: 'finding', id: fId });
              }}
              onSelectEvidence={(evId) => {
                setSelectedInspector({ kind: 'evidence', id: evId });
                updateSearch({ inspector: 'evidence', id: evId });
              }}
            />
          </aside>
        )}
      </div>

      {/* Drawer / Full-Screen Modal Inspector (<1100px) */}
      {inspectorOpen && inspectorMode !== 'dock' && (
        <Inspector
          selection={{
            kind: (activeInspector?.kind as InspectorKind) || 'evidence',
            id: activeInspector?.id ?? '',
          }}
          mode={inspectorMode}
          evidence={inspectedEvidence}
          finding={inspectedFinding}
          onClose={handleCloseInspector}
          onSelectFinding={(fId) => {
            setSelectedInspector({ kind: 'finding', id: fId });
            updateSearch({ inspector: 'finding', id: fId });
          }}
          onSelectEvidence={(evId) => {
            setSelectedInspector({ kind: 'evidence', id: evId });
            updateSearch({ inspector: 'evidence', id: evId });
          }}
        />
      )}
    </div>
  );
}
